/**
 * Install a global `unhandledrejection` listener that captures non-Error
 * promise rejections (e.g. the cryptic "Uncaught (in promise) <minified-token>")
 * and reports them to the server so they can be diagnosed.
 *
 * The listener:
 *   - logs the rejection's `typeof`, `message`, `stack`, and a JSON repr to the
 *     console (with circular-safe replacer);
 *   - POSTs the same payload to the existing `report-error` endpoint;
 *   - never throws, never preventDefault()s — the original console error stays
 *     visible so existing alerting still fires.
 *
 * Exported as a function (not auto-installed) so it is easy to unit test and so
 * tests don't accidentally double-install it.
 *
 * @param {object} deps
 * @param {{ post: Function }} deps.axios - axios instance used to POST.
 * @param {string} deps.reportUrl - endpoint to POST diagnostics to.
 * @returns {Function} an uninstall function (mostly for tests).
 */
const circularReplacer = () => {
  const seen = new WeakSet();
  return (key, value) => {
    if (typeof value === "object" && value !== null) {
      if (seen.has(value)) {
        return "[Circular]";
      }
      seen.add(value);
    }
    if (value instanceof Error) {
      return {
        name: value.name,
        message: value.message,
        stack: value.stack,
      };
    }
    return value;
  };
};

const safeStringify = value => {
  try {
    const out = JSON.stringify(value, circularReplacer());
    // JSON.stringify(undefined) returns undefined; fall back to String() so
    // callers always get a string back.
    if (out === undefined) {
      return String(value);
    }
    return out;
  } catch (_e) {
    try {
      return String(value);
    } catch (_e2) {
      return "<unstringifiable>";
    }
  }
};

const buildPayload = reason => {
  const payload = {
    error: "Unhandled promise rejection",
    reasonType: typeof reason,
    reasonString: (() => {
      try {
        return String(reason);
      } catch (_e) {
        return "<unstringifiable>";
      }
    })(),
    reasonJson: safeStringify(reason),
    route: typeof window !== "undefined" ? window.location.href : "",
  };
  if (reason instanceof Error) {
    payload.message = reason.message;
    payload.stack = reason.stack;
    payload.name = reason.name;
  } else if (reason && typeof reason === "object") {
    payload.message = reason.message;
    payload.stack = reason.stack;
  }
  return payload;
};

const installUnhandledRejectionLogger = ({ axios, reportUrl } = {}) => {
  if (typeof window === "undefined") {
    return () => {};
  }
  const handler = event => {
    try {
      const reason = event?.reason;
      const payload = buildPayload(reason);
      // eslint-disable-next-line no-console
      console.error("unhandledrejection", payload);
      if (axios && reportUrl) {
        // Fire-and-forget. Swallow any error so we don't loop.
        Promise.resolve()
          .then(() => axios.post(reportUrl, payload))
          .catch(() => {});
      }
    } catch (_e) {
      // Never let the diagnostic logger itself throw.
    }
  };
  window.addEventListener("unhandledrejection", handler);
  return () => window.removeEventListener("unhandledrejection", handler);
};

export { installUnhandledRejectionLogger, buildPayload, safeStringify };
export default installUnhandledRejectionLogger;
